import { ProjectionFilterOperationEnum } from '../enums/projection-filter-operation.enum';
import { ApiRequestProjection } from '../types/api-request-projection';
import { ProjectionFilter } from '../types/projection-filter';
import { ProjectionRelation } from '../types/projection-relation';

/**
 * Response projector utility that applies field selection and filtering to response data
 * based on projection configuration
 */
export class ApiResponseProjectorUtil {
	/**
	 * Apply projection to a single record or array of records
	 * @param data - The data to project
	 * @param projection - The projection configuration
	 * @returns The projected data
	 */
	static project<T>(data: T | T[], projection?: ApiRequestProjection): T | T[] {
		if (!projection || !data) {
			return data;
		}

		if (Array.isArray(data)) {
			return data.map((item) => this.projectSingle(item, projection));
		}

		return this.projectSingle(data, projection);
	}

	/**
	 * Apply projection to a single record
	 * @param record - The record to project
	 * @param projection - The projection configuration
	 * @returns The projected record
	 */
	private static projectSingle<T>(
		record: T,
		projection: ApiRequestProjection,
	): T {
		if (!record || typeof record !== 'object') {
			return record;
		}

		const result: any = {};

		// Apply root field selection
		if (projection.root) {
			this.applyRootProjection(record, result, projection.root);
		} else {
			// If no root projection, include all root fields
			Object.keys(record).forEach((key) => {
				if (!this.isRelationInProjection(key, projection)) {
					result[key] = (record as Record<string, unknown>)[key];
				}
			});
		}

		// Apply relation projections
		Object.keys(projection).forEach((relationName) => {
			if (relationName === 'root') {
				return; // Already handled
			}

			const relationConfig = projection[relationName];
			if (relationConfig && relationName in record) {
				result[relationName] = this.projectRelation(
					(record as Record<string, unknown>)[relationName],
					relationConfig,
				);
			}
		});

		return result as T;
	}

	/**
	 * Apply root level field projection
	 * @param source - The source record
	 * @param target - The target result object
	 * @param config - The root projection config
	 */
	private static applyRootProjection(
		source: any,
		target: any,
		config: ProjectionRelation,
	): void {
		const fields = config.fields || [];

		if (fields.length === 0 || fields.includes('*')) {
			// Include all root fields
			Object.keys(source).forEach((key) => {
				target[key] = source[key];
			});
		} else {
			// Include only specified fields
			fields.forEach((field) => {
				if (field in source) {
					target[field] = source[field];
				}
			});
		}
	}

	/**
	 * Check if a key is a relation defined in the projection
	 * @param key - The key to check
	 * @param projection - The projection configuration
	 * @returns True if the key is a relation
	 */
	private static isRelationInProjection(
		key: string,
		projection: ApiRequestProjection,
	): boolean {
		return key !== 'root' && key in projection;
	}

	/**
	 * Project a relation (nested object or array)
	 * @param relationData - The relation data
	 * @param config - The projection configuration for this relation
	 * @returns The projected relation data
	 */
	private static projectRelation(
		relationData: any,
		config: ProjectionRelation,
	): any {
		if (!relationData) {
			return relationData;
		}

		// Handle array relations
		if (Array.isArray(relationData)) {
			// Apply filters first
			let filteredData = relationData;
			if (config.filters && config.filters.length > 0) {
				filteredData = this.applyFilters(relationData, config.filters);
			}

			// Then apply field selection to each item
			return filteredData.map((item) => this.projectRelationItem(item, config));
		}

		// Handle single object relations
		return this.projectRelationItem(relationData, config);
	}

	/**
	 * Project a single item within a relation
	 * @param item - The item to project
	 * @param config - The projection configuration
	 * @returns The projected item
	 */
	private static projectRelationItem(
		item: any,
		config: ProjectionRelation,
	): any {
		if (!item || typeof item !== 'object') {
			return item;
		}

		const result: any = {};
		const fields = config.fields || [];

		if (fields.length === 0 || fields.includes('*')) {
			// Include all fields from this item
			Object.keys(item).forEach((key) => {
				result[key] = item[key];
			});
		} else {
			// Include only specified fields
			fields.forEach((field) => {
				if (field in item) {
					result[field] = item[field];
				}
			});
		}

		// Apply nested relation projections
		const relations = config.relations;
		if (relations) {
			Object.keys(relations).forEach((nestedRelationName) => {
				const nestedConfig = relations[nestedRelationName];
				if (nestedRelationName in item) {
					result[nestedRelationName] = this.projectRelation(
						item[nestedRelationName],
						nestedConfig,
					);
				}
			});
		}

		return result;
	}

	/**
	 * Apply filters to an array of items
	 * @param items - The items to filter
	 * @param filters - The filters to apply
	 * @returns The filtered items
	 */
	private static applyFilters(
		items: any[],
		filters: ProjectionFilter[],
	): any[] {
		return items.filter((item) => {
			// Item must pass all filters (AND logic)
			return filters.every((filter) => this.applyFilter(item, filter));
		});
	}

	/**
	 * Apply a single filter to an item
	 * @param item - The item to check
	 * @param filter - The filter to apply
	 * @returns True if the item passes the
	 */
	private static applyFilter(item: any, filter: ProjectionFilter): boolean {
		const fieldValue = item[filter.field];

		switch (filter.operation) {
			case ProjectionFilterOperationEnum.EQUALS:
				return fieldValue === filter.value;

			case ProjectionFilterOperationEnum.NOT_EQUALS:
				return fieldValue !== filter.value;

			case ProjectionFilterOperationEnum.CONTAINS:
				if (
					typeof fieldValue === 'string' &&
					typeof filter.value === 'string'
				) {
					return fieldValue.toLowerCase().includes(filter.value.toLowerCase());
				}
				return false;

			case ProjectionFilterOperationEnum.IN:
				if (Array.isArray(filter.value)) {
					return filter.value.includes(fieldValue);
				}
				return false;

			case ProjectionFilterOperationEnum.NOT_IN:
				if (Array.isArray(filter.value)) {
					return !filter.value.includes(fieldValue);
				}
				return true;

			case ProjectionFilterOperationEnum.GREATER_THAN:
				return fieldValue > filter.value;

			case ProjectionFilterOperationEnum.LESS_THAN:
				return fieldValue < filter.value;

			case ProjectionFilterOperationEnum.EXISTS:
				return fieldValue !== null && fieldValue !== undefined;

			default:
				return true; // Unknown operation, include the item
		}
	}
}
